import * as styles from '../FirstResponder.module.css';
import { isPositive, renderMetric } from '../LivePreview/liveMetricDisplay';
import {
  LiveHeadlineStats,
  LivePilotPolicy,
  LiveRunScope,
} from '../LivePreview/liveViewModel';

import { windowCaption } from './windowCaption';

/*
 * Canonical presentation component (GRAVY-10373 convergence, Phase 1 extraction).
 * Extracted verbatim from LiveConsole.tsx's headline-metrics card + policy summary line.
 *
 * TWO things the four tiles must state without breaking the row they share:
 *
 * 1. WHICH PERIOD. The tiles carry the short width of the Results heading's own label
 *    (windowCaption) — the full one wrapped to two and three lines inside a 4-column grid and
 *    took the numbers off their shared baseline. Nothing is hidden by the truncation: the full
 *    label is the tile's `title`, and the Results panel prints it whole.
 *
 * 2. WHICH POPULATION, where it differs. The three funnel tiles count the pilot's own cohort —
 *    the clients named on the policy line right below them. "Needs attention" does not: it is
 *    every client's unresolved queue (the console's own attention_count — see Connected/index),
 *    so an operator reading "9 offers sent / 2 needs attention" as one funnel is reading two
 *    different populations. Nothing on the wire changes and no count moves; the caption says the
 *    scope out loud, because a scope nobody states is a scope everybody assumes matches its
 *    neighbour.
 */

type Props = {
  stats: LiveHeadlineStats;
  activePolicy: LivePilotPolicy;
  // W5: which persisted run mode the two windowed tiles count. Optional so the preview and the
  // unit tests that exercise the caption alone are unchanged; the connected console always passes it.
  runScope?: LiveRunScope;
};

const HeadlineStats = ({ stats, activePolicy, runScope }: Props) => (
  <div className={styles.card}>
    <div className={styles.section}>
      <div className={styles.stats} data-testid="flp-stats">
        {/* label · stat · testid key · scope note on the face · scope note in the title */}
        {(
          [
            ['Watching', stats.watchingNow, 'watching', '', ''],
            [
              'Offers sent',
              stats.offersSent,
              'offers',
              runScope ? ` · ${runScope}` : '',
              runScope ? ` · ${runScope} runs` : '',
            ],
            [
              'Shifts filled',
              stats.shiftsFilled,
              'filled',
              runScope ? ` · ${runScope}` : '',
              runScope ? ` · ${runScope} runs` : '',
            ],
            [
              'Needs attention',
              stats.needsAttention,
              'attention',
              ', all clients',
              ', all clients',
            ],
          ] as const
        ).map(([label, stat, key, scopeShort, scopeFull]) => {
          // Full and short come from ONE function over ONE label, so the tooltip and the tile
          // face cannot name different periods — the short form is a prefix of the full one.
          // The run scope is one word on the tile face (the one-line clamp cut "shadow runs" to
          // "shadow r…" on a laptop) and the full phrase in the title, beside the full window text.
          const full = `${windowCaption(stat.windowLabel)}${scopeFull}`;
          const short = `${windowCaption(stat.windowLabel, true)}${scopeShort}`;
          return (
            <div key={key} className={styles.stat}>
              {/* The label is clamped to one line (.statLabel), so the untruncated text has to
                  stay reachable: `title` carries the whole caption, including the minutes and
                  the clock the heading names them on. */}
              <div
                className={styles.statLabel}
                title={`${label} (${full})`}
                data-testid={`flp-statlabel-${key}`}
              >
                {label}{' '}
                <span
                  className={styles.small}
                  data-testid={`flp-window-${key}`}
                >
                  ({short})
                </span>
              </div>
              <div
                className={`${styles.statValue} ${
                  key === 'attention' && isPositive(stat.value)
                    ? styles.statValueAlert
                    : ''
                }`}
                data-testid={`flp-stat-${key}`}
              >
                {renderMetric(stat.value)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    <div className={`${styles.section} ${styles.sectionSubdued}`}>
      <span
        className={`${styles.muted} ${styles.small}`}
        data-testid="flp-policy-line"
      >
        {activePolicy.clients.map((c) => c.name).join(', ')} &nbsp;·&nbsp; Waits{' '}
        {activePolicy.waitBeforeEvaluationMinutes} min after the first worker
        notification &nbsp;·&nbsp; Up to {activePolicy.maxOffersPerJob} offers
        per job &nbsp;·&nbsp; Within {activePolicy.distanceLimitMiles} miles
        &nbsp;·&nbsp; Needs {activePolicy.minLeadMinutes} minutes before the
        shift
      </span>
    </div>
  </div>
);

export default HeadlineStats;
